import { useQuery } from "@tanstack/react-query";
import { apiFetch, apiJson } from "@/api/client";
import { withQuery } from "@/api/query";
import type { PaymentStatusName } from "@/api/invoices";
import type { MailStatus } from "@/api/mail";

/* Mirrors api/src/pbo_invoicing/reports/schemas.py. Money is a string ("1400.00"). */

export type MoneyTotal = { currency: string; amount: string };

export type AgeingBucket = {
  key: string;
  label: string;
  invoices: number;
  totals: MoneyTotal[];
};

export type AgeingRow = {
  invoice_id: string;
  invoice_no: string | null;
  client_code: string;
  client_name: string;
  vessel_name: string | null;
  invoice_date: string;
  effective_due_date: string | null;
  days_overdue: number;
  bucket: string;
  awaiting_bank_letter: boolean;
  status: PaymentStatusName;
  currency: string;
  total: string;
  paid: string;
  balance: string;
  late_charge: string;
};

export type AgeingReport = {
  as_of: string;
  outstanding: MoneyTotal[];
  late_charges: MoneyTotal[];
  buckets: AgeingBucket[];
  rows: AgeingRow[];
};

export type RevenueBy = "client" | "vessel" | "month";

export type RevenueRow = {
  key: string;
  label: string;
  currency: string;
  invoices: number;
  invoiced: string;
  paid: string;
  balance: string;
  inr_credited: string;
};

export type RevenueReport = {
  by: RevenueBy;
  period_from: string | null;
  period_to: string | null;
  rows: RevenueRow[];
  totals: MoneyTotal[];
  inr_credited: string;
};

export type GstLevel = "ok" | "watch" | "high" | "exceeded";

export type GstThresholdReport = {
  fy_code: string;
  threshold_inr: string;
  turnover_inr: string;
  realised_inr: string;
  estimated_inr: string;
  percentage: number;
  level: GstLevel;
  average_rate: string | null;
  invoiced: MoneyTotal[];
  invoices: number;
  basis: string;
};

export type MonthlyPoint = {
  month: string;
  label: string;
  invoiced: string;
  received_inr: string;
};

export type ProfitMonth = {
  month: string;
  label: string;
  income_inr: string;
  expenses_inr: string;
  profit_inr: string;
};

export type BucketBalance = {
  bucket_id: string;
  name: string;
  kind: "payout" | "pool";
  income_share_inr: string;
  spent_inr: string;
  balance_inr: string;
};

/** Rupees in (credited) less rupees out (expenses). Money with no rupee figure yet is in
 *  `not_converted`, never guessed. `owed` is what payout buckets still owe, all time. */
export type ProfitReport = {
  period_from: string;
  period_to: string;
  income_inr: string;
  expenses_inr: string;
  profit_inr: string;
  margin: number | null;
  not_converted: MoneyTotal[];
  months: ProfitMonth[];
  buckets: BucketBalance[];
  owed: MoneyTotal[];
};

export type NextDue = {
  id: string;
  name: string;
  form: string;
  period_label: string;
  due_on: string;
  days: number;
};

export type ComplianceSummary = {
  overdue: number;
  due_soon: number;
  accruing_per_day_inr: string;
  accrued_inr: string;
  next_due: NextDue | null;
};

export type DashboardReport = {
  as_of: string;
  home_currency: string;
  outstanding: MoneyTotal[];
  overdue: MoneyTotal[];
  unpaid_invoices: number;
  overdue_invoices: number;
  awaiting_bank_letter: number;
  drafts: number;
  received_this_month_inr: string;
  months: MonthlyPoint[];
  gst: GstThresholdReport;
  /** This financial year to date. */
  profit: ProfitReport;
  compliance: ComplianceSummary;
  mail: MailStatus;
  /** Bank and cash accounts, per currency, today. */
  cash: MoneyTotal[];
  /** Buckets past their ceiling this month. */
  budget_over: string[];
  /** Buckets spending ahead of what clients have paid. */
  budget_fund_short: string[];
  /** What the tracker's digest lists about bills. */
  bills: BillsSummary;
  pending_transfers: number;
};

export type BillsSummary = {
  unpaid: number;
  unpaid_inr: string;
  to_reimburse: number;
  to_reimburse_inr: string;
  renewals_due: number;
  renewals_overdue: number;
};

export type AgeingFilters = { asOf?: string; clientId?: string };
export type RevenueFilters = { by: RevenueBy; from?: string; to?: string };
export type ProfitFilters = { from?: string; to?: string };

export const reportKeys = {
  all: ["reports"] as const,
  dashboard: ["reports", "dashboard"] as const,
  ageing: (filters: AgeingFilters) => ["reports", "ageing", filters] as const,
  revenue: (filters: RevenueFilters) => ["reports", "revenue", filters] as const,
  gst: (fy: string | undefined) => ["reports", "gst", fy ?? "current"] as const,
  profit: (filters: ProfitFilters) => ["reports", "profit", filters] as const,
};

// --------------------------------------------------------------------------- requests

export function getDashboard(): Promise<DashboardReport> {
  return apiJson<DashboardReport>("/reports/dashboard");
}

export function getAgeing(filters: AgeingFilters = {}): Promise<AgeingReport> {
  return apiJson<AgeingReport>(
    withQuery("/reports/ageing", { as_of: filters.asOf, client_id: filters.clientId }),
  );
}

export function getRevenue(filters: RevenueFilters): Promise<RevenueReport> {
  return apiJson<RevenueReport>(
    withQuery("/reports/revenue", { by: filters.by, from: filters.from, to: filters.to }),
  );
}

export function getGstThreshold(fy?: string): Promise<GstThresholdReport> {
  return apiJson<GstThresholdReport>(withQuery("/reports/gst-threshold", { fy }));
}

export function getProfit(filters: ProfitFilters): Promise<ProfitReport> {
  return apiJson<ProfitReport>(withQuery("/reports/profit", { from: filters.from, to: filters.to }));
}

/** The CA's workbook for a financial year, with the file name the API chose. */
export async function fetchExportRegister(fy?: string): Promise<{ blob: Blob; filename: string }> {
  const res = await apiFetch(withQuery("/reports/export-register.xlsx", { fy }));
  const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  return { blob: await res.blob(), filename: match?.[1] ?? "export-register.xlsx" };
}

// --------------------------------------------------------------------------- hooks

export function useDashboard() {
  return useQuery({ queryKey: reportKeys.dashboard, queryFn: getDashboard });
}

export function useAgeing(filters: AgeingFilters) {
  return useQuery({ queryKey: reportKeys.ageing(filters), queryFn: () => getAgeing(filters) });
}

export function useRevenue(filters: RevenueFilters) {
  return useQuery({ queryKey: reportKeys.revenue(filters), queryFn: () => getRevenue(filters) });
}

export function useGstThreshold(fy?: string) {
  return useQuery({ queryKey: reportKeys.gst(fy), queryFn: () => getGstThreshold(fy) });
}

export function useProfit(filters: ProfitFilters) {
  return useQuery({ queryKey: reportKeys.profit(filters), queryFn: () => getProfit(filters) });
}
